import { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'transmute/debounce';
import { DataFetchingClient } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UITag from 'UIComponents/tag/UITag';
import Small from 'UIComponents/elements/Small';
import UIList from 'UIComponents/list/UIList';
import { H4 } from 'UIComponents/elements/headings';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIIcon from 'UIComponents/icon/UIIcon';
import { EERIE } from 'HubStyleTokens/colors';
import AddGuestsField from '../input/AddGuestsField';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { getAllGuestEmails } from '../../selectors/AddGuestsFormSelectors';
import { addGuestEmail as addGuestEmailAction, removeGuestEmail as removeGuestEmailAction
// @ts-expect-error untyped import
} from '../../redux/actions/FormActions';
import { getCanAddGuests, getMaxGuestCount
// @ts-expect-error untyped import
} from 'MeetingsLib/selectors/BookInfoSelectors';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const client = new DataFetchingClient();
const Description = styled.span.withConfig({
  displayName: "AddGuestsForm__Description",
  componentId: "sc-14bitco-0"
})(["font-weight:500;"]);
const AddGuestsForm = () => {
  const dispatch = useDispatch();
  const guestEmails = useSelector(getAllGuestEmails);
  const canAddGuests = useSelector(getCanAddGuests);
  const maxGuestCount = useSelector(getMaxGuestCount);
  const [guestEmail, setGuestEmail] = useState('');
  const [emailToValidate, setEmailToValidate] = useState('');
  const [emailIsChanging, setEmailIsChanging] = useState(false);
  const {
    isValid: emailIsValid,
    loading: isValidatingEmail
  } = useEmailValidation({
    email: emailToValidate,
    shouldSkip: !canAddGuests,
    client
  });
  const handleInputChange = useCallback(evt => {
    const email = evt.target.value;
    setGuestEmail(email.trim().toLowerCase());
    setEmailIsChanging(true);
  }, []);
  const updateEmailToValidate = useCallback(email => {
    setEmailToValidate(email);
    setEmailIsChanging(false);
  }, []);
  const debouncedEmailToValidateChange = useMemo(() => debounce(500, nextVal => updateEmailToValidate(nextVal)), [updateEmailToValidate]);
  const handleAddGuest = useCallback(() => {
    dispatch(addGuestEmailAction(guestEmail));
    setGuestEmail('');
    setEmailToValidate('');
  }, [dispatch, guestEmail]);
  const handleRemoveGuest = useCallback(guestToDelete => {
    dispatch(removeGuestEmailAction(guestToDelete));
  }, [dispatch]);
  useEffect(() => {
    debouncedEmailToValidateChange(guestEmail);
  }, [debouncedEmailToValidateChange, guestEmail]);
  const formHeader = useMemo(() => {
    return /*#__PURE__*/_jsxs("div", {
      className: "p-x-2 p-top-4",
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        gap: 5,
        className: "p-bottom-4",
        children: [/*#__PURE__*/_jsx(H4, {
          className: "m-all-0",
          tabIndex: 0,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "prebook.details.addGuests.label"
          })
        }), /*#__PURE__*/_jsx(UITooltip, {
          "data-test-id": "meetings-add-guests-header-tooltip",
          title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "prebook.details.addGuests.privacyPolicyTooltip_jsx",
            options: {
              href: 'https://legal.hubspot.com/privacy-policy',
              external: true
            },
            elements: {
              Link: UILink
            }
          }),
          maxWidth: "250px",
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "info",
            size: "small",
            color: EERIE
          })
        })]
      }), /*#__PURE__*/_jsx(Description, {
        tabIndex: 0,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.details.addGuests.description",
          options: {
            maxCount: maxGuestCount
          }
        })
      })]
    });
  }, [maxGuestCount]);
  const guestList = useMemo(() => {
    if (guestEmails.size === 0) {
      return /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.details.addGuests.guestList"
        })
      });
    }
    return /*#__PURE__*/_jsx(UIList, {
      inline: true,
      childClassName: "p-left-0 p-right-2",
      children: guestEmails.map(guest => {
        return /*#__PURE__*/_jsx(UITag, {
          "data-selenium-test": "meetings-add-guests-form-tag",
          "data-test-id": "meetings-add-guests-form-tag",
          closeable: true,
          onCloseClick: () => {
            handleRemoveGuest(guest);
          },
          children: guest
        }, guest);
      })
    });
  }, [guestEmails, handleRemoveGuest]);
  const emailToValidateIsChanging = isValidatingEmail || emailIsChanging;
  const emailToValidateIsChangingOrEmpty = emailToValidateIsChanging || !emailToValidate;
  const isValid = emailToValidateIsChangingOrEmpty || emailIsValid;
  const emailAlreadyExists = !emailToValidateIsChanging && guestEmails.has(guestEmail);
  const renderedErrorMessage = useMemo(() => {
    if (!isValid) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.details.errorHelper.email"
      });
    }
    if (emailAlreadyExists) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.details.addGuests.emailExists"
      });
    }
    return null;
  }, [isValid, emailAlreadyExists]);
  const renderedTooltipMessage = useMemo(() => {
    if (guestEmails.size === maxGuestCount) return null;
    let tooltipMessage = '';
    if (guestEmail.length === 0) {
      tooltipMessage = 'prebook.details.addGuests.emailRequiredTooltip';
    } else if (!isValid) {
      tooltipMessage = 'prebook.details.errorHelper.email';
    } else if (emailAlreadyExists) {
      tooltipMessage = 'prebook.details.addGuests.emailExists';
    }
    return tooltipMessage ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: tooltipMessage
    }) : null;
  }, [isValid, emailAlreadyExists, guestEmail, guestEmails, maxGuestCount]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-test": "add-guests-form",
      "data-test-id": "add-guests-form",
      id: "add-guests-form",
      className: "p-x-6 p-y-4",
      children: [formHeader, /*#__PURE__*/_jsx(UIFlex, {
        children: /*#__PURE__*/_jsx(AddGuestsField, {
          guestEmail: guestEmail,
          guestCount: guestEmails.size,
          errorMessage: renderedErrorMessage,
          tooltipMessage: renderedTooltipMessage,
          isValidatingEmail: emailToValidateIsChanging,
          maxGuestCount: maxGuestCount,
          handleAddGuest: handleAddGuest,
          handleInputChange: handleInputChange
        })
      }), /*#__PURE__*/_jsx(UIFlex, {
        className: "p-x-2 p-top-4",
        children: guestList
      })]
    })
  });
};
export default AddGuestsForm;